import StringUtils from './strings';

/**
 * Figures out which display role should be shown beside a user's name in forum
 * posts, in gallery comments, etc.
 *
 * Note that display roles are different than the roles returned by the backend.
 * For example, backend roles don't differentiate between foundation students
 * and intermediate students.
 *
 * For the most accurate results, provide the user's roles and current term.
 *
 * @param {object}   options
 * @param {string[]} options.roles
 * @param {string[]} options.[termIds]
 * @param {Term}     options.[currentTerm] - see src/services/terms
 * @returns {string|null}
 */
export function getDisplayRole(options) {

    if (!options) {
        return null;
    }
    options = Object.assign({}, options);

    if (!options.roles || options.roles.length === 0) {
        return null;
    }

    if (typeof options.termIds === 'number') options.termIds = options.termIds.toString();
    
    if (typeof options.termIds === 'string') {
        options.termIds = options.termIds.split(',');
    }

    // Normalize out-dated or identical roles
    const mappedRoles = {
        'OnlineDirector': 'Director',
        'GlobalMentor': 'Mentor',
        'Instructor': 'Faculty',  
        'LessonAuditor': 'Director',
        'CourseAdmin': 'Director'   
    };
    options.roles = options.roles.map(r => mappedRoles[r] || r);
    // Pick a preferred role as the initial display role
    const orderedRoles = [
        'Admin',
        'Director',
        'Faculty',
        'Supervisor',
        'Mentor',
        'Support',
        'Student',
        'CampusStudent',
        'CampusRegistrant',
        'CampusInstructor',
        'Registrant',
        'Recruiter',
        'LessonAuditor',
        'CourseAdmin',
        'TA'
    ];
    let displayRole = orderedRoles.find(r => options.roles.includes(r));

    // Use the given term info to change "Student" to "OnlineFoundation", etc.
    const studentRoles = [
        'Student',
        'CampusStudent',
    ];
    if (studentRoles.includes(displayRole)) {

        const isOnline = displayRole === 'Student';

        if (options && options.termIds) {
            const termNumToRole = {
                0: 'Preterm',
                1: 'Foundation',
                2: 'Intermediate',
                3: 'Advanced',
                4: 'Mentorship',
            };
            displayRole = termNumToRole[options.termIds.length] || displayRole;

            if (isOnline) {
                displayRole = 'Online' + displayRole;
            }
            else {
                displayRole = 'Campus' + displayRole;
            }
        }

        if (options && options.currentTerm) {
            displayRole = StringUtils.toTitleCase(options.currentTerm.type);

            if (isOnline) {
                displayRole = 'Online' + displayRole;
            }
            else {
                displayRole = 'Campus' + displayRole;
            }
        }
    }

    // Change "OnlineFoundation" to "Online Foundation"
    displayRole = StringUtils.splitTitleCase(displayRole);

    return displayRole;
}

/**
 * Picks a matching icon to show along with the user's display role.
 *
 * Accepts all the same options as `getDisplayRole()`, or you can provide the
 * display role directly.
 *
 * @param {object} options - see `getDisplayRole()` for additional options
 * @param {string} options.[displayRole]
 * @returns {string|null}
 */
export function getDisplayRoleIcon(options) {

    if (!options) {
        return null;
    }

    const displayRole = options.displayRole || getDisplayRole(options);
    if (!displayRole) {
        return null;
    }


    const fileRoot = 'rank-' + displayRole.toLowerCase().replace(/\s+/g, '-');

    if (window.location.hostname === 'localhost') {
        return '/static/img/ranks/' + fileRoot + '.svg';
    }

    // TODO: change this back
    if (displayRole === 'Registrant') {
        return '/static/static/img/ranks/' + fileRoot + '-1.svg';
    } else {
        return '/static/static/img/ranks/' + fileRoot + '.svg';
    }
}

export default {
    getDisplayRole,
    getDisplayRoleIcon,
};



// WEBPACK FOOTER //
// ./src/utils/users.js